/**
 * src/utils/handoffPayload.ts — the decoder for VAF's opaque `hp` param.
 *
 * Mirrors vercel-auth-functions `lib/handoffPayload.ts` — read that file for why this
 * exists. Short version: every magic-link email goes out through Iterable's
 * click-tracking wrapper, which can decode the callback URL one extra layer. `client` and
 * `redirectTo` used to ride as their own flat query params, and an extra decode can turn
 * an encoded `&` inside one of them back into a real separator — splitting it from
 * whatever came after it, which is exactly what happened to `redirectTo` in a real sign-in
 * while `org_slug` (first, nothing after it to split from) came through fine.
 *
 * VAF now packs both values into one base64url string instead. Base64url's alphabet
 * (`[A-Za-z0-9_-]`) has no character an extra decode can act on, so the value cannot
 * fracture no matter how many times it gets decoded. The two files must stay
 * byte-compatible — same param name, same JSON shape — which is why this one is a mirror
 * rather than an independent implementation.
 */

/** The query parameter the opaque payload rides in. Same name VAF writes it under. */
export const HANDOFF_PAYLOAD_PARAM = 'hp';

export interface HandoffPayload {
  client?: string;
  redirectTo?: string;
}

/**
 * Decode the opaque handoff param, or `null` when absent or malformed.
 *
 * Malformed is not an error to surface — it is the shape "nothing usable arrived",
 * indistinguishable from absent to every caller of this function, and readers already
 * treat that as an empty candidate rather than a failure (this app has no error boundary,
 * so an unguarded throw here would blank the screen mid-flow).
 */
export function decodeHandoffPayload(raw: string | null | undefined): HandoffPayload | null {
  if (!raw) {
    return null;
  }
  try {
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const parsed = JSON.parse(atob(base64));
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { client, redirectTo } = parsed as Record<string, unknown>;
    const result: HandoffPayload = {};
    if (typeof client === 'string' && client) {
      result.client = client;
    }
    if (typeof redirectTo === 'string' && redirectTo) {
      result.redirectTo = redirectTo;
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}
